import { Injectable } from '@angular/core';
import { Observable, of, forkJoin, catchError, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CourseService, Course } from '../instructor/course.service';
import { UnitsService, Unit } from '../instructor/units.service';
import { buildPreviewCourseShell } from '../shared/components/course-player-learner-modal/pure-functions/preview-course-shell';
import { unitDisplayName } from '../shared/components/course-player-learner-modal/pure-functions/unit-display-name';
import { ModuleService, Module } from '../instructor/module.service';
import { ProfileService } from '../profile/profile.service';
import { roundCourseProgress } from '../shared/utils/progress-display.utils';
import { resolveFeedbackFlags } from '../shared/utils/feedback-flags.util';
import { FeedbackFlags } from '../instructor/course-builder-dynamic/types/module-creation.types';

// Types for course player
export type PlayerMode = 'learner' | 'instructor_preview' | 'instructor_test';
export type ItemType = 'unit' | 'module' | 'unit_intro' | 'unit_quiz' | 'course_summary';
export type ItemStatus = 'completed' | 'current' | 'locked' | 'available';

/** KAN-353 task-5.3 backend gate codes on the player read routes. */
export type CourseEntitlementBlockCode = 'COURSE_ACCESS_EXPIRED' | 'COURSE_ACCESS_REQUIRED';

/**
 * True when an error is the task-5.3 entitlement 403 (paid course, access
 * expired or never purchased). These must NOT be swallowed into an "empty
 * course" render - the player shows the blocked/buy state instead (KAN-353).
 */
export function entitlementBlockCodeFrom(error: any): CourseEntitlementBlockCode | null {
  const code = error?.error?.error?.code ?? error?.error?.code;
  if (error?.status === 403 && (code === 'COURSE_ACCESS_EXPIRED' || code === 'COURSE_ACCESS_REQUIRED')) {
    return code;
  }
  return null;
}

// Navigation item interface
export interface CourseNavigationItem {
  id: string;
  name: string;
  type: ItemType;
  status: ItemStatus;
  
  // Hierarchy info
  parentUnitId?: string;
  parentUnitName?: string;  // Name of the parent unit (for display purposes)
  isDirectModule: boolean;  // True if module is directly attached to course
  
  // Navigation info
  globalIndex: number;      // Overall position in course sequence
  unitIndex?: number;       // Position within unit (for unit modules)
  
  // Content info
  hasVideo?: boolean;
  hasQuiz?: boolean;
  isPlayable: boolean;      // False for unit intro/containers
  
  // Content data
  videoPath?: string;
  bunnyVideoId?: string;
  /** Per-org Bunny CDN host (videos.cdn_hostname) for this item's video. Lets
   *  the player build the HLS playlist on the org's own host instead of the
   *  shared host (which 404s for per-org videos). Undefined for legacy/
   *  shared-library videos. */
  cdnHostname?: string;
  videoTitle?: string;
  videoDuration?: number;  // Duration in seconds
  quizId?: string;         // Quiz ID for submission
  quiz?: any[];
  quizTitle?: string;
  /** Quiz description shown in the learner quiz panel (KAN-224). Not part of
   *  the course-structure payload — populated by the KAN-191 live quiz refetch
   *  (LearnerQuizService.refreshLiveQuizData) when the learner opens the item. */
  quizDescription?: string;
  requiredWatchPercentage?: number; // Percentage of video required before quiz access (0-100, 0 = no requirement)
  description?: string;
  passingScore?: number;
  /** Feedback visibility level for this item's quiz (KAN-69 / KAN-126 / KAN-219).
   *  Drives the quiz-panel's canShow* gates in instructor preview. For real
   *  learner sessions the value also arrives via the submission response, but
   *  preview is in-memory only — without this field the panel falls back to
   *  'indicators_answers' and Preview ignores the saved setting. */
  feedbackVisibility?: 'score_only' | 'indicators' | 'indicators_answers' | 'indicators_answers_explanations';
  /** KAN-343: the three authoritative feedback flags for this item's quiz.
   *  Drives the quiz-panel canShow* gates directly (instructor preview + the
   *  seed for a real learner session). Derived with a legacy-scalar fallback. */
  feedbackFlags?: FeedbackFlags;
  /** KAN-328: conditional answer reveal — when true, correct answers/explanations
   *  are withheld from a learner who did not pass. Layers on top of
   *  feedbackVisibility; drives the quiz-panel gate in client-scored preview. */
  revealAnswersOnlyOnPass?: boolean;
  /** Custom incorrect ("Note from instructor") message for this item's quiz (KAN-309).
   *  Drives the quiz-panel's failure banner in instructor preview. For real learner
   *  sessions the gated text arrives via the submission response; preview grades
   *  client-side and has no response, so without this field the banner never renders
   *  in Preview Mode. */
  wrongAnswerFeedback?: string | null;
  /** Custom pass ("success") message for this item's quiz (KAN-327). Drives the
   *  quiz-panel's success banner in instructor preview; the learner path sources
   *  the gated text from the submission response. Mirror of wrongAnswerFeedback. */
  passFeedback?: string | null;

  // Progress
  progress?: {
    videoProgress: number;
    quizPassed: boolean;
    completed: boolean;
  };

  /**
   * Quiz submission tracking (separate from completion status)
   *
   * Lifecycle:
   * - PHASE 1 (loadCourseProgress): Initialized from backend progress data when courseProgressMap has quizScore
   * - PHASE 2 (isQuizCompleted): Checked to determine if quiz answers should be pre-filled
   * - PHASE 3 (handleQuizSubmit): Set to true immediately after quiz submission
   *
   * Relationship to status field:
   * - hasSubmission: Tracks ANY quiz submission (passed OR failed)
   * - status: Only 'completed' for PASSED quizzes
   *
   * Use case: Enables answer pre-fill for both passed AND failed quiz attempts
   *
   * @implements REQ-GRADE-203 - Quiz answer pre-fill for all submissions
   */
  hasSubmission?: boolean;

  // Unit quiz completion timestamp from backend (for displaying completion status)
  unitQuizCompletedAt?: string;

  // For units that contain modules
  children?: CourseNavigationItem[];  // Modules within this unit
  unitHasQuiz?: boolean;
}

// Player configuration
export interface PlayerConfig {
  enforceSequential: boolean;
  enforceVideoCompletion: boolean;
  saveProgress: boolean;
  allowRetake: boolean;
  showCorrectAnswers: boolean;
  showTechnicalInfo?: boolean;
  allowJumpNavigation: boolean;
}

// Next action after completion
export interface NextAction {
  type: 'next_module' | 'unit_complete' | 'course_complete' | 'unit_quiz';
  itemId?: string;
  unitId?: string;
}

// Course structure with progress
export interface CourseStructure {
  courseId: string;
  courseName: string;
  navigationItems: CourseNavigationItem[];
  currentItemId?: string;
  progress: {
    completedItems: number;
    totalItems: number;
    percentComplete: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CoursePlayerService {

  // Player mode configurations
  private readonly PLAYER_CONFIGS: Record<PlayerMode, PlayerConfig> = {
    learner: {
      enforceSequential: true,
      enforceVideoCompletion: true,
      saveProgress: true,
      allowRetake: false,
      showCorrectAnswers: false,
      allowJumpNavigation: false
    },
    instructor_preview: {
      enforceSequential: true,
      enforceVideoCompletion: true,
      saveProgress: false,
      allowRetake: true,
      showCorrectAnswers: true,
      showTechnicalInfo: true,
      allowJumpNavigation: false
    },
    instructor_test: {
      enforceSequential: true,
      enforceVideoCompletion: true,
      saveProgress: false,
      allowRetake: true,
      showCorrectAnswers: true,
      showTechnicalInfo: true,
      allowJumpNavigation: false
    }
  };

  constructor(
    private courseService: CourseService,
    private unitsService: UnitsService,
    private moduleService: ModuleService,
    private profileService: ProfileService
  ) {}

  /**
   * Get player configuration for a specific mode
   */
  getPlayerConfig(mode: PlayerMode): PlayerConfig {
    return { ...this.PLAYER_CONFIGS[mode] };
  }

  /**
   * Get complete course structure with navigation items
   */
  getCourseStructure(courseId: string): Observable<CourseStructure> {
    console.log('[CoursePlayerService] Getting course structure for courseId:', courseId);
    return this.courseService.getCourse(courseId).pipe(
      switchMap(course => {
        console.log('[CoursePlayerService] Course loaded:', course);
        // Get course content (units and direct modules)
        return forkJoin({
          course: of(course),
          content: this.getCourseContent(courseId)
        });
      }),
      map(({ course, content }) => {
        console.log('[CoursePlayerService] Course content loaded:', content);
        const navigationItems = this.buildNavigationSequence(course, content, content.lockMap);
        console.log('[CoursePlayerService] Navigation items built:', navigationItems);
        
        return {
          courseId: course.id,
          courseName: course.title || course.name || 'Untitled Course',
          navigationItems,
          progress: this.calculateProgress(navigationItems)
        };
      })
    );
  }

  /**
   * Build a CourseStructure synchronously from a single hydrated Unit, without
   * touching the backend. Used by instructor_preview mode when previewing a
   * unit from the Content Hub library (no courseId available).
   *
   * The Unit IS the source of truth — we wrap it in a minimal Course-shaped
   * shell and reuse the same buildNavigationSequence pipeline as real courses.
   */
  buildStructureFromUnit(unit: Unit): CourseStructure {
    const courseShell = buildPreviewCourseShell(unit);
    const navigationItems = this.buildNavigationSequence(
      courseShell as unknown as Course,
      { units: [unit], directModules: [] }
    );

    return {
      courseId: courseShell.id,
      courseName: courseShell.title,
      navigationItems,
      progress: this.calculateProgress(navigationItems)
    };
  }

  /**
   * Get course content (units + direct modules) in the correct order
   */
  private getCourseContent(courseId: string): Observable<any> {
    console.log('[CoursePlayerService] Getting course content for courseId:', courseId);
    return forkJoin({
      units: this.unitsService.getUnitsByCourse(courseId).pipe(
        map(response => {
          console.log('[CoursePlayerService] Units response:', response);
          return response.data || [];
        }),
        catchError(error => {
          // KAN-353: the entitlement 403 must reach the player's blocked
          // state; only non-entitlement failures keep the resilient swallow.
          if (entitlementBlockCodeFrom(error)) {
            return throwError(() => error);
          }
          console.warn('[CoursePlayerService] Failed to load units for course:', courseId, error);
          return of([]);
        })
      ),
      allModules: this.moduleService.getModulesByCourse(courseId).pipe(
        map(modules => {
          console.log('[CoursePlayerService] All modules response:', modules);
          return modules;
        }),
        catchError(error => {
          if (entitlementBlockCodeFrom(error)) {
            return throwError(() => error);
          }
          console.warn('[CoursePlayerService] Failed to load modules for course:', courseId, error);
          return of([]);
        })
      )
    }).pipe(
      map(({ units, allModules }) => {
        // Build lock map from allModules (annotated by /modules endpoint)
        const lockMap = new Map<string, boolean>();
        allModules.forEach((mod: any) => {
          if (mod.locked !== undefined) {
            lockMap.set(mod.id, mod.locked);
          }
        });

        // Get IDs of all modules that belong to units
        const unitModuleIds = new Set<string>();
        units.forEach((unit: any) => {
          if (unit.modules && Array.isArray(unit.modules)) {
            unit.modules.forEach((module: any) => {
              unitModuleIds.add(module.id);
            });
          }
        });

        // Filter out modules that are already in units - only keep true direct modules
        const directModules = allModules.filter((module: any) => !unitModuleIds.has(module.id));

        console.log('[CoursePlayerService] Filtered direct modules:', directModules);
        console.log('[CoursePlayerService] Unit module IDs:', Array.from(unitModuleIds));
        console.log('[CoursePlayerService] Lock map entries:', lockMap.size);

        return { units, directModules, lockMap };
      })
    );
  }

  /**
   * Build the complete navigation sequence accounting for mixed hierarchy
   */
  private buildNavigationSequence(course: Course, content: any, lockMap?: Map<string, boolean>): CourseNavigationItem[] {
    const sequence: CourseNavigationItem[] = [];
    let globalIndex = 0;
    
    // Get units and direct modules
    const units = content.units || [];
    const directModules = content.directModules || [];
    
    console.log('🎯 [CoursePlayer] Building navigation sequence');
    console.log('🎯 [CoursePlayer] Units:', units);
    console.log('🎯 [CoursePlayer] Direct modules:', directModules);
    
    // KAN-671: an empty course gets an empty sequence. A synthetic placeholder
    // item here was typed 'module', so every module counter counted it
    // ("Total Modules 1" against "0 modules" on My Courses). The sidebar and
    // the content area render their own empty states instead.
    if (units.length === 0 && directModules.length === 0) {
      console.warn('No units or modules found for course:', course.id);
      return sequence;
    }
    
    // Combine units and direct modules with their order indices
    const allItems: any[] = [];
    
    // Add units with their order index
    for (const unit of units) {
      allItems.push({
        type: 'unit',
        data: unit,
        orderIndex: unit.orderIndex || unit.unitOrder || 0
      });
    }

    // Add direct modules with their order index
    for (const module of directModules) {
      allItems.push({
        type: 'module',
        data: module,
        orderIndex: module.orderIndex || 0
      });
    }
    
    // Sort all items by their order index
    allItems.sort((a, b) => a.orderIndex - b.orderIndex);
    
    console.log('🎯 [CoursePlayer] Combined and sorted items by order_index:', allItems.map(item => ({
      type: item.type,
      name: item.data.name || item.data.title,
      orderIndex: item.orderIndex
    })));
    
    // Process items in the correct order
    for (const item of allItems) {
      if (item.type === 'module') {
        // Process direct module
        const module = item.data;
        // Handle quiz data - backend now returns camelCase
        const quizData = (module as any).quiz || (module as any).quizQuestions || (module as any).questions || [];
        const hasQuiz = !!(quizData && quizData.length > 0) || !!(module as any).quizId || !!(module as any).hasQuiz;

        console.log('[CoursePlayerService] Processing direct module:', {
          id: module.id,
          name: (module as any).name || (module as any).title,
          videoName: (module as any).videoName,
          videoDuration: (module as any).videoDuration,
          quizTitle: (module as any).quizTitle,
          quizName: (module as any).quizName,
          hasQuiz,
          quizId: (module as any).quizId,
          quizDataLength: quizData.length,
          quizData: quizData.length > 0 ? 'Quiz data present' : 'No quiz data',
          allModuleFields: Object.keys(module)
        });

        sequence.push({
          id: module.id,
          name: (module as any).name || (module as any).title,
          type: 'module',
          status: lockMap?.get(module.id) ? 'locked' : 'available',
          isDirectModule: true,
          globalIndex: globalIndex++,
          isPlayable: true,
          hasVideo: !!(module as any).videoPath || !!(module as any).bunnyVideoId,
          hasQuiz: hasQuiz,
          videoPath: (module as any).videoPath,
          bunnyVideoId: (module as any).bunnyVideoId,
          // Per-org Bunny: carry the video's own CDN host onto the nav item so
          // the split-screen player builds the HLS playlist on the org's host.
          cdnHostname: (module as any).cdnHostname,
          videoTitle: (module as any).videoName || (module as any).name || (module as any).title,
          videoDuration: (module as any).videoDuration,
          quizId: (module as any).quizId,
          quizTitle: (module as any).quizTitle || (module as any).quizName || 'Module Quiz',
          quiz: quizData,
          // KAN-338: prefer the backend's EFFECTIVE (post course-override)
          // percentage when present so the course player - and every learner
          // surface fed by it (timeline badge, quiz-panel messaging,
          // split-screen player) - gates and displays what's actually
          // enforced, not just this module's own raw setting.
          requiredWatchPercentage:
            (module as any).effectiveRequiredWatchPercentage ??
            (module as any).effective_required_watch_percentage ??
            (module as any).requiredWatchPercentage ??
            (module as any).required_watch_percentage ??
            90,
          // CRITICAL FIX: Use nullish coalescing (??) to preserve 0 values, logical OR (||) treats 0 as falsy
          passingScore: (module as any).quizPassingScore ?? (module as any).quiz_passing_score ?? 80,
          // KAN-219: carry the quiz's configured feedback-visibility level onto the
          // nav item so instructor preview ("Preview Mode" / "Take a course") honors
          // `score_only` the same way the learner path does. Left undefined when the
          // source omits it — the consuming modal applies its `|| 'indicators_answers'`
          // default. (Learners additionally get the authoritative value from the
          // submission response after grading.)
          feedbackVisibility: (module as any).quizFeedbackVisibility ?? (module as any).quiz_feedback_visibility,
          // KAN-343: carry the authoritative feedback flags onto the nav item
          // (legacy-scalar fallback baked in) so preview + learner gate on them.
          feedbackFlags: resolveFeedbackFlags({
            showIndicators: (module as any).quizShowIndicators ?? (module as any).quiz_show_indicators,
            showAnswers: (module as any).quizShowAnswers ?? (module as any).quiz_show_answers,
            showExplanations: (module as any).quizShowExplanations ?? (module as any).quiz_show_explanations,
            feedbackVisibility: (module as any).quizFeedbackVisibility ?? (module as any).quiz_feedback_visibility
          }),
          revealAnswersOnlyOnPass: (module as any).quizRevealAnswersOnlyOnPass ?? (module as any).quiz_reveal_answers_only_on_pass,
          // KAN-309: carry the custom incorrect message so instructor preview's
          // failure banner ("Note from instructor") renders the same as a learner's.
          wrongAnswerFeedback: (module as any).quizWrongAnswerFeedback ?? (module as any).quiz_wrong_answer_feedback ?? null,
          // KAN-327: carry the custom pass message so instructor preview's success banner renders.
          passFeedback: (module as any).quizPassFeedback ?? (module as any).quiz_pass_feedback ?? null
        });
      } else if (item.type === 'unit') {
        // Process unit and its modules
        const unit = item.data;
        // KAN-655: one resolved name for every surface fed by this unit's items.
        const unitName = unitDisplayName(unit);
        // V1 Requirement: Always add unit introduction page for every unit
        // This ensures navigation always starts at the unit preview (index 1/X)
        // The unit title is displayed even if there's no description
        sequence.push({
          id: `${unit.id}_intro`,
          name: `${unitName} - Overview`,
          type: 'unit_intro',
          status: 'available',
          parentUnitId: unit.id,
          parentUnitName: unitName,
          isDirectModule: false,
          globalIndex: globalIndex++,
          isPlayable: false,  // Unit intro is informational
          description: unit.description || ''  // May be empty, but unit title still displays
        });
        
        // Add all modules within the unit
        const unitModules = unit.modules || [];
        for (const module of unitModules) {
          // Handle quiz data - backend now returns camelCase
          const quizData = (module as any).quiz || (module as any).quizQuestions || (module as any).questions || [];
          const hasQuiz = !!(quizData && quizData.length > 0) || !!(module as any).quizId || !!(module as any).hasQuiz;

          sequence.push({
            id: module.id,
            name: (module as any).name || (module as any).title,
            type: 'module',
            status: lockMap?.get(module.id) ? 'locked' : 'available',
            parentUnitId: unit.id,
            parentUnitName: unitName,
            isDirectModule: false,
            globalIndex: globalIndex++,
            unitIndex: (module as any).orderIndex || (module as any).order || 0,
            isPlayable: true,
            hasVideo: !!(module as any).videoPath || !!(module as any).bunnyVideoId || !!(module as any).hasVideo,
            hasQuiz: hasQuiz,
            videoPath: (module as any).videoPath,
            bunnyVideoId: (module as any).bunnyVideoId,
            // Per-org Bunny: carry the video's own CDN host (see direct branch).
            cdnHostname: (module as any).cdnHostname,
            videoTitle: (module as any).videoName || (module as any).name || (module as any).title,
            videoDuration: (module as any).videoDuration,
            quizId: (module as any).quizId,
            quizTitle: (module as any).quizTitle || (module as any).quizName || 'Module Quiz',
            quiz: quizData,
            // KAN-338: prefer the backend's EFFECTIVE (post course-override)
            // percentage when present (see direct-module branch above).
            requiredWatchPercentage:
              (module as any).effectiveRequiredWatchPercentage ??
              (module as any).effective_required_watch_percentage ??
              (module as any).requiredWatchPercentage ??
              (module as any).required_watch_percentage ??
              90,
            // CRITICAL FIX: Use nullish coalescing (??) to preserve 0 values, logical OR (||) treats 0 as falsy
            passingScore: (module as any).quizPassingScore ?? (module as any).quiz_passing_score ?? 80,
            // KAN-219: see direct-module branch above — carry feedback-visibility so
            // instructor preview honors `score_only`.
            feedbackVisibility: (module as any).quizFeedbackVisibility ?? (module as any).quiz_feedback_visibility,
            // KAN-343: carry the authoritative feedback flags (see direct-module branch).
            feedbackFlags: resolveFeedbackFlags({
              showIndicators: (module as any).quizShowIndicators ?? (module as any).quiz_show_indicators,
              showAnswers: (module as any).quizShowAnswers ?? (module as any).quiz_show_answers,
              showExplanations: (module as any).quizShowExplanations ?? (module as any).quiz_show_explanations,
              feedbackVisibility: (module as any).quizFeedbackVisibility ?? (module as any).quiz_feedback_visibility
            }),
            revealAnswersOnlyOnPass: (module as any).quizRevealAnswersOnlyOnPass ?? (module as any).quiz_reveal_answers_only_on_pass,
            // KAN-309: carry the custom incorrect message (see direct-module branch).
            wrongAnswerFeedback: (module as any).quizWrongAnswerFeedback ?? (module as any).quiz_wrong_answer_feedback ?? null,
            // KAN-327: carry the custom pass message (see direct-module branch).
            passFeedback: (module as any).quizPassFeedback ?? (module as any).quiz_pass_feedback ?? null
          });
        }

        // Add unit quiz if exists (handle both snake_case and camelCase field names)
        console.log('🔍 [CoursePlayer] Unit quiz check:', {
          unitId: unit.id,
          unitName,
          has_unit_quiz: unit.has_unit_quiz,
          hasUnitQuiz: unit.hasUnitQuiz,
          unit_quiz_id: unit.unit_quiz_id,
          unitQuizId: unit.unitQuizId,
          unit_quiz_title: unit.unit_quiz_title,
          unitQuizTitle: unit.unitQuizTitle,
          allKeys: Object.keys(unit),
          fullUnit: unit
        });
        const hasUnitQuiz = unit.has_unit_quiz || unit.hasUnitQuiz || !!unit.unit_quiz_id || !!unit.unitQuizId;
        if (hasUnitQuiz) {
          // Parse and validate unit quiz questions
          const unitQuizQuestions = unit.unitQuizQuestions || unit.unit_quiz_questions || [];
          let parsedQuizQuestions = [];
          // KAN-386: bank-draw slots serialize to { slotType: 'bank', bankId, ... }
          // with NO type/text, so the validity filter below strips them all. Track
          // them here (outside the try) so an EOU quiz built entirely from bank
          // draws is still surfaced — its real questions are drawn at attempt-start.
          let bankDrawSlots: any[] = [];
          // KAN-414: every well-formed slot in AUTHORED ORDER (authored questions
          // and bank slots interleaved as the instructor arranged them) — the
          // payload a bank-draw quiz must carry so the draw resolves it in place.
          let allSlots: any[] = [];

          try {
            // Backend may return as string (JSONB) or already parsed
            parsedQuizQuestions = typeof unitQuizQuestions === 'string'
              ? JSON.parse(unitQuizQuestions)
              : unitQuizQuestions;

            // Ensure it's an array
            if (!Array.isArray(parsedQuizQuestions)) {
              console.warn(`[CoursePlayer] Unit quiz questions not an array for unit ${unit.id}`);
              parsedQuizQuestions = [];
            }

            // KAN-386: capture bank-draw slots from the UNFILTERED array before
            // the validity filter strips them (they carry no type/text). A quiz
            // may mix authored + bank slots, so this is independent of the count
            // of static questions that survive the filter.
            allSlots = parsedQuizQuestions.filter((q: any) => q && typeof q === 'object');
            bankDrawSlots = allSlots.filter(
              (q: any) => q.slotType === 'bank' || q.sourceType === 'bank'
            );

            // Validate question structure. KAN-414: bank slots are excluded FIRST —
            // per REQ-QUIZ-311 they are not questions for the content validators, so
            // reporting them as "missing required fields" (and, for a pure bank-draw
            // quiz, "All N questions invalid") was misleading log noise about a
            // perfectly valid quiz. Only authored questions are validated here.
            const authoredSlots = allSlots.filter(
              (q: any) => !(q.slotType === 'bank' || q.sourceType === 'bank')
            );
            parsedQuizQuestions = authoredSlots.filter((q, idx) => {
              if (!q.type || !q.text) {
                console.warn(`[CoursePlayer] Question missing required fields at index ${idx} for unit ${unit.id}`);
                return false;
              }
              return true;
            });

            if (parsedQuizQuestions.length === 0 && authoredSlots.length > 0) {
              console.error(`[CoursePlayer] All ${authoredSlots.length} questions invalid for unit ${unit.id}`);
            }
          } catch (error) {
            console.error(`[CoursePlayer] Failed to parse unit quiz questions for unit ${unit.id}:`, error);
            parsedQuizQuestions = [];
          }

          // KAN-386: surface the quiz when it has usable static questions OR any
          // bank-draw slot. A pure bank-draw EOU quiz has zero static questions
          // (all stripped by the filter) but must still appear — mirroring
          // module-level bank-draw quizzes, whose real questions are drawn at
          // attempt-start (quiz-panel.startOrResumeAttempt), NOT at read time. A
          // unit with a quiz id but ZERO questions AND ZERO slots is still skipped.
          const hasBankDrawSlots = bankDrawSlots.length > 0;
          if (parsedQuizQuestions.length > 0 || hasBankDrawSlots) {
            sequence.push({
              id: `${unit.id}_quiz`,
              name: unit.unit_quiz_title || unit.unitQuizTitle || `${unitName} - Unit Assessment`,
              type: 'unit_quiz',
              status: 'available',
              parentUnitId: unit.id,
              parentUnitName: unitName,
              isDirectModule: false,
              globalIndex: globalIndex++,
              isPlayable: true,
              hasQuiz: true,
              quizId: unit.unit_quiz_id || unit.unitQuizId,
              quizTitle: unit.unit_quiz_title || unit.unitQuizTitle,
              // KAN-386: carry a non-empty array so the quiz panel's first-mount
              // attempt-start fires (it triggers on a non-empty quiz array, exactly
              // like module-level bank draws); the draw then replaces these slots
              // with the resolved questions (KAN-414: the panel refuses to render
              // an unresolved slot, so they are never shown as empty cards).
              // KAN-414: when ANY bank slot exists, carry EVERY slot in authored
              // order — authored and bank alike. Preferring the filtered array
              // dropped the bank slots from a MIXED quiz, which under-rendered the
              // preview and lost the `slotType:'bank'` marker that makes a failed
              // learner draw fail CLOSED. Matches the module-level branch, which
              // passes its array unfiltered.
              quiz: hasBankDrawSlots ? allSlots : parsedQuizQuestions,
              // CRITICAL FIX: Use nullish coalescing (??) to preserve 0 values
              passingScore: unit.unitQuizPassingScore ?? unit.unit_quiz_passing_score ?? 80,
              // KAN-219: carry the unit quiz's feedback-visibility so instructor
              // preview honors `score_only` (see direct-module branch).
              feedbackVisibility: unit.unitQuizFeedbackVisibility ?? unit.unit_quiz_feedback_visibility,
              // KAN-343: carry the unit quiz's authoritative feedback flags (see direct-module branch).
              feedbackFlags: resolveFeedbackFlags({
                showIndicators: unit.unitQuizShowIndicators ?? unit.unit_quiz_show_indicators,
                showAnswers: unit.unitQuizShowAnswers ?? unit.unit_quiz_show_answers,
                showExplanations: unit.unitQuizShowExplanations ?? unit.unit_quiz_show_explanations,
                feedbackVisibility: unit.unitQuizFeedbackVisibility ?? unit.unit_quiz_feedback_visibility
              }),
              revealAnswersOnlyOnPass: unit.unitQuizRevealAnswersOnlyOnPass ?? unit.unit_quiz_reveal_answers_only_on_pass,
              // KAN-309: carry the unit quiz's custom incorrect message so instructor
              // preview's failure banner renders (see direct-module branch).
              wrongAnswerFeedback: unit.unitQuizWrongAnswerFeedback ?? unit.unit_quiz_wrong_answer_feedback ?? null,
              // KAN-327: carry the unit quiz's custom pass message (see direct-module branch).
              passFeedback: unit.unitQuizPassFeedback ?? unit.unit_quiz_pass_feedback ?? null,
              // BUGFIX: Store unit quiz completion timestamp from backend
              unitQuizCompletedAt: unit.unit_quiz_completed_at || unit.unitQuizCompletedAt
            });

            console.log('🎯 [CoursePlayer] Added unit quiz with questions:', {
              unitId: unit.id,
              quizId: unit.unit_quiz_id || unit.unitQuizId,
              questionCount: parsedQuizQuestions.length
            });
          } else {
            console.warn(`[CoursePlayer] Unit ${unit.id} has quiz ID but no questions - skipping quiz nav item`);
          }
        }
      }
    }
    
    console.log('🎯 [CoursePlayer] Final navigation sequence order:');
    sequence.forEach((item, index) => {
      if (item.parentUnitId) {
        console.log(`  ${index + 1}. 📄 ${item.name} (module in unit ${item.parentUnitId})`);
      } else {
        console.log(`  ${index + 1}. ${item.type === 'unit_intro' ? '📋' : '📄'} ${item.name} (${item.isDirectModule ? 'direct module' : item.type})`);
      }
    });
    
    return sequence;
  }

  /**
   * Get the next item in the sequence
   */
  getNextItem(currentId: string, sequence: CourseNavigationItem[]): CourseNavigationItem | null {
    const currentIndex = sequence.findIndex(item => item.id === currentId);
    if (currentIndex === -1 || currentIndex === sequence.length - 1) return null;
    
    const currentItem = sequence[currentIndex];
    const nextItem = sequence[currentIndex + 1];
    
    // Check if next item is accessible based on current completion
    if (this.canAccessNextItem(currentItem, nextItem, sequence)) {
      return nextItem;
    }
    
    return null;
  }

  /**
   * Get the previous item in the sequence
   */
  getPreviousItem(currentId: string, sequence: CourseNavigationItem[]): CourseNavigationItem | null {
    const currentIndex = sequence.findIndex(item => item.id === currentId);
    if (currentIndex <= 0) return null;
    
    return sequence[currentIndex - 1];
  }

  /**
   * Check if user can access the next item
   */
  canAccessNextItem(
    currentItem: CourseNavigationItem, 
    nextItem: CourseNavigationItem,
    sequence: CourseNavigationItem[],
    mode: PlayerMode = 'learner'
  ): boolean {
    
    const config = this.getPlayerConfig(mode);
    
    // Instructor preview can access anything
    if (!config.enforceSequential) {
      return true;
    }
    
    // If in same unit, check if current item is completed
    if (currentItem.parentUnitId && currentItem.parentUnitId === nextItem.parentUnitId) {
      return currentItem.status === 'completed';
    }
    
    // If moving to different unit or direct module, check unit completion
    if (currentItem.parentUnitId && !nextItem.parentUnitId) {
      // Moving from unit to direct module - ensure unit is complete
      return this.isUnitComplete(currentItem.parentUnitId, sequence);
    }
    
    // Direct module to direct module
    if (!currentItem.parentUnitId && !nextItem.parentUnitId) {
      return currentItem.status === 'completed';
    }
    
    // Direct module to unit - just check current completion
    return currentItem.status === 'completed';
  }

  /**
   * Check if an entire unit is completed
   */
  isUnitComplete(unitId: string, sequence: CourseNavigationItem[]): boolean {
    const unitItems = sequence.filter(item => item.parentUnitId === unitId);
    return unitItems.every(item => item.status === 'completed');
  }

  /**
   * Calculate overall course progress
   */
  private calculateProgress(items: CourseNavigationItem[]): { completedItems: number; totalItems: number; percentComplete: number } {
    const playableItems = items.filter(item => item.isPlayable);
    const completedItems = playableItems.filter(item => item.status === 'completed').length;
    const totalItems = playableItems.length;
    // KAN-146 AC3: canonical 1-dp rule (matches the timeline + backend surfaces),
    // not integer Math.round which produced "67" for a 2/3 (66.7) course.
    const percentComplete = roundCourseProgress(completedItems, totalItems);

    return { completedItems, totalItems, percentComplete };
  }

  /**
   * Determine what should happen after item completion
   */
  determineNextAction(
    completedItem: CourseNavigationItem, 
    sequence: CourseNavigationItem[]
  ): NextAction {
    
    // Check if this completes a unit
    if (completedItem.parentUnitId) {
      const unitItems = sequence.filter(item => item.parentUnitId === completedItem.parentUnitId);
      const completedUnitItems = unitItems.filter(item => 
        item.status === 'completed' || item.id === completedItem.id
      );
      
      // If this completes the unit
      if (completedUnitItems.length === unitItems.length) {
        const nextItem = this.getNextItem(completedItem.id, sequence);
        
        return {
          type: 'unit_complete',
          unitId: completedItem.parentUnitId,
          itemId: nextItem?.id
        };
      }
    }
    
    // Check for next item
    const nextItem = this.getNextItem(completedItem.id, sequence);
    
    if (!nextItem) {
      return { type: 'course_complete' };
    }
    
    // Special handling for unit quiz
    if (nextItem.type === 'unit_quiz') {
      return {
        type: 'unit_quiz',
        itemId: nextItem.id
      };
    }
    
    return {
      type: 'next_module',
      itemId: nextItem.id
    };
  }

  /**
   * Check if item can be accessed (placeholder)
   */
  canAccessItem(itemId: string, timeline: CourseNavigationItem[], mode: PlayerMode): boolean {
    const config = this.getPlayerConfig(mode);
    
    // Instructor preview can access anything
    if (!config.enforceSequential) {
      return true;
    }
    
    // For learners, implement sequential logic
    const item = timeline.find(t => t.id === itemId);
    return item ? item.status !== 'locked' : false;
  }

  /**
   * Get current position in course (placeholder)
   */
  getCurrentPosition(courseId: string, userId: string): Observable<string> {
    // TODO: Implement actual current position tracking
    return of('');
  }

  /**
   * Resolves navigation item ID to the correct progress map key.
   *
   * CRITICAL: Unit quizzes use synthetic IDs (unitId_quiz) for navigation,
   * but progress data is keyed by the actual unit ID in the database.
   *
   * This helper ensures consistent progress lookups across the application.
   *
   * @param item - The navigation item to get the progress key for
   * @returns The correct key to use for progress map lookups
   *
   * @example
   * // For a unit quiz with navigation ID "abc123_quiz"
   * getProgressKey(unitQuizItem) // Returns: "abc123"
   *
   * // For a regular module with ID "module456"
   * getProgressKey(moduleItem) // Returns: "module456"
   *
   * @implements REQ-QUIZ-201 - Unit quiz progress tracking
   */
  getProgressKey(item: CourseNavigationItem): string {
    if (item.type === 'unit_quiz' && item.parentUnitId) {
      // Unit quizzes: use parent unit ID as progress key
      return item.parentUnitId;
    }
    // All other items: use their own ID
    return item.id;
  }

  /**
   * Populate progress map with unit quiz completion data
   *
   * BACKGROUND: Unit quizzes store completion timestamps in the units table
   * (unit_quiz_completed_at), which are loaded into navigation items during
   * getCourseStructure(). However, the module progress API does NOT include
   * unit quiz completion because unit quizzes aren't modules.
   *
   * This method bridges that gap by extracting unit quiz completion from
   * navigation items and adding it to the progress map using the parent
   * unit ID as the key (via getProgressKey()).
   *
   * @param navigationItems - Course navigation items from getCourseStructure()
   * @param courseProgressMap - Progress map to populate
   */
  populateUnitQuizCompletion(
    navigationItems: CourseNavigationItem[],
    courseProgressMap: Map<string, any>
  ): void {
    navigationItems.forEach(item => {
      if (item.type === 'unit_quiz') {
        if (item.unitQuizCompletedAt) {
          const progressKey = this.getProgressKey(item);
          const existingProgress = courseProgressMap.get(progressKey) || {};

          courseProgressMap.set(progressKey, {
            ...existingProgress,
            quizPassed: true,
            quizCompletedAt: item.unitQuizCompletedAt,
            moduleId: item.id,
            completed: true,
            moduleCompleted: true
          });
          console.log('[CoursePlayerService] Populated unit quiz completion:', progressKey, 'from nav item:', item.id);
        } else {
          console.log('[CoursePlayerService] Unit quiz has no completion timestamp:', item.id);
        }
      }
    });
  }
}